import React, { useMemo, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { makeAuthedRequest } from "shared/api/authRequest";
import useGetUser from "helpers/useGetUser";
import { isAdmin } from "helpers";
import SearchInput from "components/shared/SearchInput";
import { AdminTable } from "components/shared/AdminTable";
import { statusBadgeVariant } from "shared/utils/statusChipColor";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuTrigger,
} from "components/ui/dropdown-menu";
import PropertyCreateModal from "./PropertyCreateModal";
import { PROPERTY_STATUS_OPTIONS as STATUS_OPTIONS } from "./hooks/usePropertyData";
import type { PropertyStatus } from "./hooks/usePropertyData";
import { Badge } from "components/ui/badge";
import { Button } from "components/ui/button";
import { Filter } from "lucide-react";

interface PropertyListResponse {
  propertyExternalId: string;
  name: string;
  physicalKeyExtension: boolean;
  sites: string[];
  groupCount: number;
  unitCount: number;
  residentCount: number;
  staffCount: number;
  status?: PropertyStatus | null;
}

const DEFAULT_STATUS_FILTER: PropertyStatus[] = ["Staging", "Active"];

async function fetchProperties(): Promise<PropertyListResponse[]> {
  const data = await makeAuthedRequest<PropertyListResponse[]>({
    method: "get",
    url: "/api/Property",
  });
  return data;
}

export default function Properties() {
  const { data: user } = useGetUser(true);
  const admin = isAdmin(user);

  const {
    data,
    isLoading,
    isError,
  } = useQuery({ queryKey: ["properties"], queryFn: fetchProperties, enabled: admin });

  const [sortKey, setSortKey] = useState<keyof PropertyListResponse>("name");
  const [sortDir, setSortDir] = useState<"asc" | "desc">("asc");
  const [search, setSearch] = useState("");
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [statusFilter, setStatusFilter] = useState<PropertyStatus[]>(DEFAULT_STATUS_FILTER);
  const [filterMenuOpen, setFilterMenuOpen] = useState(false);

  const activeFilterCount = statusFilter.length;

  const toggleStatusFilter = (status: PropertyStatus) => {
    setStatusFilter((currentFilters) => {
      if (currentFilters.includes(status)) {
        return currentFilters.length === 1
          ? currentFilters
          : currentFilters.filter((currentStatus) => currentStatus !== status);
      }

      return [...currentFilters, status];
    });
  };

  const sortedData = useMemo(() => {
    let list = [...(data || [])];

    list = list.filter((p) => statusFilter.includes(p.status ?? "Staging"));

    // Fuzzy search filter
    if (search.trim()) {
      const searchLower = search.toLowerCase();
      list = list.filter((p) => {
        const externalId = (p.propertyExternalId ?? "").toLowerCase();
        const name = (p.name ?? "").toLowerCase();
        const sites = (p.sites || []).join(", ").toLowerCase();
        return (
          externalId.includes(searchLower) ||
          name.includes(searchLower) ||
          sites.includes(searchLower)
        );
      });
    }

    const normalize = (val: any): any => {
      if (Array.isArray(val)) return val.join(", ").toLowerCase();
      if (typeof val === "string") return val.toLowerCase();
      if (typeof val === "boolean") return val ? 1 : 0;
      return val ?? "";
    };
    list.sort((a, b) => {
      const aVal = normalize(a[sortKey]);
      const bVal = normalize(b[sortKey]);
      if (aVal < bVal) return sortDir === "asc" ? -1 : 1;
      if (aVal > bVal) return sortDir === "asc" ? 1 : -1;
      return 0;
    });
    return list;
  }, [data, sortKey, sortDir, search, statusFilter]);

  const handleSort = (key: keyof PropertyListResponse) => {
    if (key === sortKey) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      setSortDir("asc");
    }
  };

  const sortIndicator = (key: keyof PropertyListResponse) =>
    sortKey === key ? (sortDir === "asc" ? " ▲" : " ▼") : "";

  if (!admin) {
    return <Navigate to="/" replace />;
  }

  return (
    <main className="space-y-6">
      <header className="pb-5 border-b border-slate-200 sm:flex sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-semibold leading-7 text-slate-900">
            Properties
          </h1>
        </div>
        <div>
          <Button
            className="mt-4 sm:mt-0"
            type="button"
            onClick={() => setShowCreateModal(true)}
          >
            Add Property
          </Button>
        </div>
      </header>
      <div>
        <AdminTable
          aria-label="Properties"
          toolbar={
            <div className="flex flex-col gap-4 px-4 py-4 lg:flex-row lg:items-center lg:justify-between">
              <SearchInput
                className="w-full h-10 px-3 text-sm transition-colors bg-white border rounded-md outline-none border-slate-200 text-slate-800 placeholder:text-slate-400 focus:border-blue-500 focus:ring-2 focus:ring-blue-100 sm:w-96"
                placeholder="Search by external ID, name, or sites"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                aria-label="Search properties"
              />
              <DropdownMenu onOpenChange={setFilterMenuOpen} open={filterMenuOpen}>
                <DropdownMenuTrigger asChild>
                  <button
                    type="button"
                    className="inline-flex items-center justify-center h-10 gap-2 px-3 text-sm font-medium transition-colors bg-white border rounded-md border-slate-200 text-slate-700 hover:border-slate-300 hover:bg-slate-50"
                  >
                    <Filter className="size-4" />
                    Filter{activeFilterCount === STATUS_OPTIONS.length ? "" : ` (${activeFilterCount})`}
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuLabel>Filter by status</DropdownMenuLabel>
                  {STATUS_OPTIONS.map((status) => {
                    const isSelected = statusFilter.includes(status);
                    const isOnlySelected = isSelected && statusFilter.length === 1;

                    return (
                      <DropdownMenuCheckboxItem
                        key={status}
                        checked={isSelected}
                        disabled={isOnlySelected}
                        onSelect={(event) => {
                          event.preventDefault();
                          toggleStatusFilter(status);
                        }}
                      >
                        {status}
                      </DropdownMenuCheckboxItem>
                    );
                  })}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          }
        >
          <thead>
            <tr>
              <th scope="col" className="cursor-pointer select-none" onClick={() => handleSort("propertyExternalId")}>
                External ID{sortIndicator("propertyExternalId")}
              </th>
              <th scope="col" className="cursor-pointer select-none" onClick={() => handleSort("name")}>
                Name{sortIndicator("name")}
              </th>
              <th scope="col" className="cursor-pointer select-none" onClick={() => handleSort("status")}>
                Status{sortIndicator("status")}
              </th>
              <th scope="col" className="cursor-pointer select-none" onClick={() => handleSort("physicalKeyExtension")}>
                Physical Key Extension{sortIndicator("physicalKeyExtension")}
              </th>
              <th scope="col" className="cursor-pointer select-none" onClick={() => handleSort("sites")}>
                Sites{sortIndicator("sites")}
              </th>
              <th scope="col" className="text-right cursor-pointer select-none" onClick={() => handleSort("groupCount")}>
                Group Count{sortIndicator("groupCount")}
              </th>
              <th scope="col" className="text-right cursor-pointer select-none" onClick={() => handleSort("unitCount")}>
                Unit Count{sortIndicator("unitCount")}
              </th>
              <th scope="col" className="text-right cursor-pointer select-none" onClick={() => handleSort("residentCount")}>
                Resident Count{sortIndicator("residentCount")}
              </th>
              <th scope="col" className="text-right cursor-pointer select-none" onClick={() => handleSort("staffCount")}>
                Staff Count{sortIndicator("staffCount")}
              </th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr>
                <td colSpan={9}>
                  <div className="flex items-center justify-center text-sm min-h-80 text-slate-500">
                    Loading properties...
                  </div>
                </td>
              </tr>
            ) : isError ? (
              <tr>
                <td colSpan={9}>
                  <div className="flex items-center justify-center px-4 text-sm text-center min-h-80 text-rose-700">
                    There was an error loading properties.
                  </div>
                </td>
              </tr>
            ) : sortedData.length === 0 ? (
              <tr>
                <td colSpan={9} className="py-16 text-sm text-center text-slate-500">
                  No properties match your search.
                </td>
              </tr>
            ) : (
              sortedData.map((p) => (
                <tr key={p.propertyExternalId}>
                  <td>
                    <Link to={`/admin/properties/${p.propertyExternalId}`}>{p.propertyExternalId}</Link>
                  </td>
                  <td>
                    <Link to={`/admin/properties/${p.propertyExternalId}`}>{p.name}</Link>
                  </td>
                  <td>
                    <Badge variant={statusBadgeVariant(p.status)}>
                      {p.status ?? "Staging"}
                    </Badge>
                  </td>
                  <td>{p.physicalKeyExtension ? "Yes" : "No"}</td>
                  <td>{(p.sites || []).join(", ")}</td>
                  <td className="text-right">{p.groupCount}</td>
                  <td className="text-right">{p.unitCount}</td>
                  <td className="text-right">{p.residentCount}</td>
                  <td className="text-right">{p.staffCount}</td>
                </tr>
              ))
            )}
          </tbody>
        </AdminTable>
      </div>
      <PropertyCreateModal show={showCreateModal} onHide={() => setShowCreateModal(false)} />
    </main>
  );
}
